import { useState } from 'react'

import ResidentDetailsTable from '../../components/Resident/ResidentDetailsTable'
import ResidentAccessModal from '../../components/Resident/ResidentAccessModal'
import PaymentHistoryModal from '../../components/Resident/PaymentHistoryModal'
import AccessHistoryModal from '../../components/Resident/AccessHistoryModal'
import AccessHistoryDetailModal from '../../components/Resident/AccessHistoryDetailModal'

import { ResidentDTO, PaymentHistory } from '../../interfaces/resident'
import ResidentService from '../../api/resident-service'
import { calculateLevyPeriod } from '../../utils/formatDate'

interface Props {
  residentId: string
  resident?: ResidentDTO | null
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
}

const ResidentDetailsPage = ({ resident: selectedResident, handleViewSelect }: Props) => {
  const [openAccessModal, setOpenAccessModal] = useState(false)
  const [openPaymentModal, setOpenPaymentModal] = useState(false)
  const [openAccessHistoryModal, setOpenAccessHistoryModal] = useState(false)
  const [selectedAccessHistory, setSelectedAccessHistory] = useState<any>(null)
  const [openAccessDetailModal, setOpenAccessDetailModal] = useState(false)
  const [selectedAccessPropertyMemberId, setSelectedAccessPropertyMemberId] =
  useState<string | null>(null)
  const [selectedPropertyMemberId, setSelectedPropertyMemberId] = useState<string | null>(
    null
  )
  const [selectedProperty, setSelectedProperty] = useState<{
    id: string
    property: string
  } | null>(null)

  const handleSelectAccessHistory = (item: any) => {
    setSelectedAccessHistory(item)
    setOpenAccessDetailModal(true)
  }

  const handleBack = () => {
    handleViewSelect('view')
  }

 const handlePaymentHistory = (property: {
  id: string
  property: string
}) => {
  setSelectedProperty(property)
  setOpenPaymentModal(true)
}

const handleAccessHistory = (property: {
  id: string
  property: string
}) => {
  setSelectedProperty(property)
  const membership = membershipData?.results?.find((item) => {
    return (
      item._id === property.id ||
      item.property?.id === property.id ||
      item.property?._id === property.id
    )
  })

  if (!membership) {
    console.warn('No membership found for property:', property.id)
    setSelectedPropertyMemberId(null)
    return
  }

  setSelectedPropertyMemberId(membership._id)
  setOpenAccessHistoryModal(true)
}

 const {
  data: accessLogsData,
  error: accessLogsError,
  isLoading: accessLogsLoading,
} = ResidentService.useGetResidentAccessLogs(selectedPropertyMemberId ?? undefined)


  const communityMemberId = selectedResident?.id || ''

  const {
    data: membershipData,
    error: membershipError,
    isLoading: membershipLoading,
    mutate: refreshMembership,
  } = ResidentService.useGetResidentMembership(communityMemberId)


  const handleAccessControl = (property: {
  id: string
  property: string
}) => {
  setSelectedProperty(property)

  const membership = membershipData?.results?.find(
    (item) => item.property?.id === property.id
  )

  const propertyMemberId =
    membership?._id

  setSelectedAccessPropertyMemberId(
    propertyMemberId || null
  )

  setOpenAccessModal(true)
}


  if (!selectedResident) {
    return (
      <div className="mt-6 flex items-center justify-center sm:mt-10">
        <p className="text-sm font-medium">Resident not found</p>
      </div>
    )
  }

  const paymentHistory: PaymentHistory[] =
    membershipData?.results?.map((membership) => {
      const accessStatus = membership.propertyMemberAccess?.status?.toLowerCase()

      return {
        id: membership._id,
        date:
          membership.propertyMemberAccess?.enabledAt || membership.dateOfOccupancy || '',
        levyPeriod: calculateLevyPeriod(
          membership.propertyMemberAccess?.enabledAt,
          membership.duration
        ),
        amount: membership.propertyMemberAccess?.amount || 0,
        status:
          accessStatus === 'enabled'
            ? 'Paid'
            : accessStatus === 'disabled'
              ? 'Unpaid'
              : accessStatus === 'indefinite'
                ? 'Paid'
              : '-',
      }
    }) || []

  const mappedAccessLogs =
    accessLogsData?.results?.map((log) => ({
      id: log._id,
      dateTime: new Date(log.timestamp).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
         hour12: true,
      }),
      propertyAddress: log.propertyAddress || 'N/A',
      residentName: log.residentName || 'N/A',
        action:
      log.action === 'enabled'
        ? 'Access Enabled'
        : log.action === 'disabled'
          ? 'Access Disabled'
          : log.action === 'indefinite'
            ? 'Indefinite Access'
          : log.action,
      reason: log.reason || 'N/A',
     status:
  log.action === 'enabled'
    ? 'Enabled'
    : log.action === 'disabled'
      ? 'Disabled'
      : log.action === 'indefinite'
        ? 'Indefinite'
        : log.action || 'N/A',
      triggeredBy: log.triggeredBy === 'staff' ? 'Staff' : log.triggeredBy === 'system' ? 'System' : 'N/A',
    })) || []

  return (
    <>
      <div className="mt-6 grid gap-y-6 sm:mt-10 sm:gap-y-10">
        <header className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-3 sm:gap-4"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <div className="text-base font-bold sm:text-lg">Residents</div>
          </button>
        </header>

        <ResidentDetailsTable
          resident={selectedResident}
          membershipData={membershipData}
          membershipLoading={membershipLoading}
          membershipError={membershipError}
         onAccessControl={handleAccessControl}
          onPaymentHistory={handlePaymentHistory}
          onAccessHistory={handleAccessHistory}
        />
      </div>

      {openAccessModal && (
        <ResidentAccessModal
          open={openAccessModal}
          close={() => {
      setOpenAccessModal(false)
      setSelectedProperty(null)
    }}
          resident={selectedResident}
          address={selectedProperty?.property}
          handleViewSelect={handleViewSelect}
          propertyMemberId={selectedAccessPropertyMemberId ?? undefined}
          onAccessUpdated={refreshMembership}
        />
      )}

      {openPaymentModal && (
        <PaymentHistoryModal
          open={openPaymentModal}
           close={() => {
      setOpenPaymentModal(false)
      setSelectedProperty(null)
    }}
          resident={selectedResident}
          address={selectedProperty?.property}
          properties={paymentHistory}
        />
      )}

      {openAccessHistoryModal && (
        <AccessHistoryModal
          open={openAccessHistoryModal}
         onClose={() => {
      setOpenAccessHistoryModal(false)
      setSelectedPropertyMemberId(null)
      setSelectedProperty(null)
    }}
          resident={selectedResident}
          address={selectedProperty?.property}
          properties={mappedAccessLogs}
          propertyMemberId={selectedPropertyMemberId ?? undefined}
          onSelectAccessHistory={handleSelectAccessHistory}
        />
      )}

      {openAccessDetailModal && (
        <AccessHistoryDetailModal
          open={openAccessDetailModal}
          onClose={() => setOpenAccessDetailModal(false)}
          data={selectedAccessHistory}
        />
      )}
    </>
  )
}

export default ResidentDetailsPage
